/**
 * Post-auth intent/routing — universal, client-safe helpers for the coach
 * onboarding funnel's first-run intent fork (U5/U6/U7 of the onboarding-funnel
 * plan). Owns: the cookie names + TTL carrying intent/lane/destination
 * through Clerk's full-page OAuth/magic-link redirects (which bypass the
 * login page's `onSuccess`), the intent/lane enums + validators,
 * `validateRedirectPath` — the open-redirect guard for any `?redirect=`
 * value or cookie-carried destination — and `resolvePostAuthDestination`,
 * the pure landing-resolution precedence used by login.
 *
 * Cookies here are written **client-side** (`document.cookie`), mirroring
 * the `refCode` cookie pattern (`src/components/referral-capture.tsx` +
 * `REF_CODE_COOKIE_NAME`): entry-point routes are client routes, so a true
 * `httpOnly` cookie can't be set from them. The real defense is server-side —
 * `consumePostAuthIntent` (`src/lib/server/user-intent.ts`) validates every
 * cookie value against the enums/path-shape rules below before it's ever
 * trusted, so a tampered cookie can only ever resolve to one of the allowed
 * enum values or a same-origin relative path, never anything else.
 *
 * Aliases: post-auth routing, intent cookies, dcm_intent, dcm_lane,
 * dcm_destination, validateRedirectPath, setPostAuthCookies,
 * readPostAuthCookie, INTENT_FORK_LAUNCH_AT, resolvePostAuthDestination,
 * landing resolution, default landing
 * @module post-auth-routing
 */
import { z } from "zod";

// ---------------------------------------------------------------------------
// Cookie names + TTL
// ---------------------------------------------------------------------------

export const POST_AUTH_COOKIE_NAMES = {
  intent: "dcm_intent",
  lane: "dcm_lane",
  destination: "dcm_destination",
} as const;

/** Cookies survive a signup/login round trip; 30 minutes covers OAuth/magic-link redirects with room to spare. */
export const POST_AUTH_COOKIE_MAX_AGE_SECONDS = 30 * 60;

// ---------------------------------------------------------------------------
// Intent-fork launch config
// ---------------------------------------------------------------------------

/**
 * The Phase-B deploy timestamp for the coach-onboarding-funnel intent fork.
 * Accounts created before this date never see the fork (R27) — their intent
 * is backfilled by inference instead (coach record → coach, else client).
 * Accounts created on/after this date with no resolved intent are left null
 * for the U6 fork to catch.
 *
 * Placeholder value — the activation checklist (plan §Operational Notes)
 * stamps the real deploy time when Phase B ships.
 */
export const INTENT_FORK_LAUNCH_AT = new Date("2026-07-07T00:00:00.000Z");

// ---------------------------------------------------------------------------
// Value enums
// ---------------------------------------------------------------------------

export const PRIMARY_INTENT_VALUES = ["client", "coach"] as const;
export const primaryIntentSchema = z.enum(PRIMARY_INTENT_VALUES);
export type PrimaryIntent = z.infer<typeof primaryIntentSchema>;

export const ACQUISITION_LANE_VALUES = ["self_serve", "program"] as const;
export const acquisitionLaneSchema = z.enum(ACQUISITION_LANE_VALUES);
export type AcquisitionLane = z.infer<typeof acquisitionLaneSchema>;

/** U6 first-run fork answers — "exploring" records as client intent. */
export const forkAnswerSchema = z.enum(["client", "coach", "exploring"]);
export type ForkAnswer = z.infer<typeof forkAnswerSchema>;

/** Parse a raw (possibly tampered) cookie value against the intent enum. Returns null when invalid/absent. */
export const parsePrimaryIntentCookie = (
  value: string | undefined | null,
): PrimaryIntent | null => {
  const result = primaryIntentSchema.safeParse(value);
  return result.success ? result.data : null;
};

/** Parse a raw (possibly tampered) cookie value against the lane enum. Returns null when invalid/absent. */
export const parseAcquisitionLaneCookie = (
  value: string | undefined | null,
): AcquisitionLane | null => {
  const result = acquisitionLaneSchema.safeParse(value);
  return result.success ? result.data : null;
};

// ---------------------------------------------------------------------------
// Redirect-path validation (open-redirect hardening)
// ---------------------------------------------------------------------------

/**
 * Validate a redirect/destination path: only a relative, same-origin path is
 * accepted. Returns the path unchanged when valid, otherwise null.
 *
 * Rejects:
 * - anything not starting with exactly one "/" (absolute URLs, scheme-relative
 *   `//host`, bare strings)
 * - protocol-relative paths (`//evil.com`)
 * - backslashes anywhere (`/\evil.com` — browsers normalize `\` to `/`,
 *   defeating a same-origin assumption)
 * - control characters, CR/LF included (header/response-splitting vectors).
 *   Tabs matter too: URL parsers drop them, so `/<tab>/evil.com` resolves as
 *   the protocol-relative `//evil.com`
 * - `..` path segments (traversal)
 * - a scheme prefix (`https:`, `javascript:`, etc.) appearing before the path
 */
export const validateRedirectPath = (path: string): string | null => {
  if (typeof path !== "string" || path.length === 0) return null;
  if (!path.startsWith("/")) return null;
  if (path.startsWith("//")) return null;
  if (path.includes("\\")) return null;
  // oxlint-disable-next-line no-control-regex -- matching them is the point
  if (/[\u0000-\u001F\u007F]/.test(path)) return null;
  if (path.includes("..")) return null;
  // A colon before the first "/" or "?" would indicate a scheme
  // (e.g. "/\thttps://evil.com" already caught above by the backslash check;
  // this catches "/:" style tricks like "/ https://..." after trimming isn't
  // applicable here since we already require a leading "/").
  if (/^\/[^/?#]*:/.test(path)) return null;

  return path;
};

// ---------------------------------------------------------------------------
// Client-side cookie write helper
// ---------------------------------------------------------------------------

export interface SetPostAuthCookiesParams {
  readonly intent?: PrimaryIntent;
  readonly lane?: AcquisitionLane;
  readonly destination?: string;
}

/**
 * Write the post-auth intent/lane/destination cookies from a client entry
 * point (discovery results, /become-a-coach). SameSite=Lax + Secure — the
 * closest a client-side write can get to the plan's original `httpOnly`
 * language; `httpOnly` cookies cannot be set from client JS. The server-side
 * validation in `consumePostAuthIntent` is the actual defense against a
 * tampered value. No-ops on the server (SSR) since `document` is unavailable.
 */
export const setPostAuthCookies = (params: SetPostAuthCookiesParams): void => {
  if (typeof document === "undefined") return;

  const secure = window.location.protocol === "https:" ? "; secure" : "";
  const base = `path=/; max-age=${POST_AUTH_COOKIE_MAX_AGE_SECONDS}; samesite=lax${secure}`;

  if (params.intent) {
    document.cookie = `${POST_AUTH_COOKIE_NAMES.intent}=${params.intent}; ${base}`;
  }
  if (params.lane) {
    document.cookie = `${POST_AUTH_COOKIE_NAMES.lane}=${params.lane}; ${base}`;
  }
  if (params.destination) {
    const validated = validateRedirectPath(params.destination);
    if (validated) {
      document.cookie = `${POST_AUTH_COOKIE_NAMES.destination}=${encodeURIComponent(validated)}; ${base}`;
    }
  }
};

/**
 * Read a single post-auth cookie value by name, client-side only. Returns
 * null on the server (no `document`) or when the cookie is absent — callers
 * still need to validate the raw value against the relevant enum/shape
 * before trusting it (e.g. `parseAcquisitionLaneCookie`).
 */
export const readPostAuthCookie = (
  name: (typeof POST_AUTH_COOKIE_NAMES)[keyof typeof POST_AUTH_COOKIE_NAMES],
): string | null => {
  if (typeof document === "undefined") return null;

  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`));
  if (!match) return null;

  return decodeURIComponent(match.slice(name.length + 1));
};

// ---------------------------------------------------------------------------
// Post-login landing resolution (U7)
// ---------------------------------------------------------------------------

export interface ResolvePostAuthDestinationParams {
  /** Already-validated `dcm_destination` cookie value (or null when absent/invalid). */
  readonly cookieDestination: string | null;
  /** Already-validated `?redirect=` search param (or null when absent/invalid). */
  readonly searchRedirect: string | null;
  /** The account's resolved `primaryIntent` (null when still unresolved — the U6 fork owns that case). */
  readonly intent: PrimaryIntent | null;
  /** Whether the account has a coach record (tier-1, any status). */
  readonly isCoach: boolean;
}

/**
 * Pure landing-resolution precedence for post-login navigation (R10):
 *
 * 1. An explicit, validated destination wins outright — a cookie-carried
 *    destination (discovery/program deep links surviving OAuth's full-page
 *    redirect) beats a `?redirect=` search param, since the cookie is the
 *    more deliberate signal (set by an entry point, not just whatever page
 *    demanded auth).
 * 2. Coach intent with a coach record already on file → the coach home,
 *    `/my-coaching`, whose index picks Welcome or (for a revoked coach) the
 *    dashboard, so that decision lives in one place. Coach intent with no coach record yet → the registration
 *    resume step (`/become-a-coach`; the slim form pre-fills from any prior
 *    partial input per U6) rather than the coach home, which would just
 *    bounce.
 * 3. Otherwise → the client home (`/dashboard`) — covers client intent and
 *    the not-yet-resolved-intent case (the `_user` layout gate is what
 *    actually redirects an unresolved intent to `/welcome`; this function
 *    only picks a landing surface, it never decides fork eligibility).
 */
export function resolvePostAuthDestination(
  params: ResolvePostAuthDestinationParams,
): string {
  if (params.cookieDestination) return params.cookieDestination;
  if (params.searchRedirect) return params.searchRedirect;

  if (params.intent === "coach") {
    return params.isCoach ? "/my-coaching" : "/become-a-coach";
  }

  return "/dashboard";
}

// ---------------------------------------------------------------------------
// Destination path splitting (TanStack Router `navigate({ to })` shape)
// ---------------------------------------------------------------------------

/** A resolved destination split into TanStack Router's `to`/`search` navigate shape. */
export interface SplitDestination {
  readonly pathname: string;
  readonly search: Record<string, string>;
}

/**
 * Splits a validated destination path (which may carry an embedded query
 * string, e.g. `/become-a-coach?src=program` — `validateRedirectPath`
 * explicitly allows these) into the `{ to, search }` shape `useNavigate`
 * expects. `navigate({ to })` treats its whole string as a path template —
 * an embedded `?query` is never parsed out, so passing a destination with a
 * query string straight through as `to` produces a literal, broken path
 * segment instead of a query param. Always same-origin/relative (the input
 * has already passed `validateRedirectPath`), so `URL` only needs a base to
 * parse against, never to resolve one from context.
 */
export function splitDestinationPath(destination: string): SplitDestination {
  const url = new URL(destination, "http://localhost");
  return {
    pathname: url.pathname,
    search: Object.fromEntries(url.searchParams),
  };
}
